/**
 * Google link provider.
 *
 * A Google Sheet link is the worst case of all: not only does the preview fetch
 * fail, it "succeeds" — an unauthenticated fetch of a shared doc is redirected
 * to the sign-in page, whose metadata is a title like "Google Sheets" or
 * "Sign in - Google Accounts" and a description advertising Google Drive. So the
 * card ends up claiming the trip's budget spreadsheet is called "Google Sheets".
 *
 * Nothing in a Docs URL reveals the document's name, so the honest rendering is
 * a clean product label — and the caption the trip owner wrote on the bookmark,
 * which is the only real title available. Maps links are the exception: they
 * carry the place name in the path, which is worth pulling out.
 *
 * See richLinks.ts for the rendering this feeds.
 */
import { registerProvider, type RichLink } from './richLinks';

interface GoogleProduct {
  brand: string;
  variant: string;
  /** Used as the title when there is nothing better. */
  label: string;
}

// Labels are deliberately brand-free — the badge next to them already says
// "Sheets", and "Sheets · Google Sheet" reads like a stutter.
const DOCS_PRODUCTS: Record<string, GoogleProduct> = {
  spreadsheets: { brand: 'Sheets', variant: 'is-gsheets', label: 'Spreadsheet' },
  document: { brand: 'Docs', variant: 'is-gdocs', label: 'Document' },
  presentation: { brand: 'Slides', variant: 'is-gslides', label: 'Slide deck' },
  forms: { brand: 'Forms', variant: 'is-gforms', label: 'Form' },
  drawings: { brand: 'Drawings', variant: 'is-gdocs', label: 'Drawing' },
};

/**
 * Titles and descriptions Google serves to a signed-out fetcher. None of them
 * describe the document, so any of them is worse than our own label.
 */
function isGoogleJunkTitle(text: string): boolean {
  const t = text.trim();
  return (
    /^sign in/i.test(t) ||
    /google accounts/i.test(t) ||
    /^google (sheets|docs|slides|forms|drive|drawings)$/i.test(t) ||
    /one place for all your files/i.test(t) ||
    /^(untitled (spreadsheet|document|presentation))$/i.test(t)
  );
}

/** "Blue+Bottle+Coffee" → "Blue Bottle Coffee". */
function prettifyPlace(seg: string): string {
  let text: string;
  try {
    text = decodeURIComponent(seg);
  } catch {
    text = seg;
  }
  return text.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
}

function mapsLink(url: URL): RichLink {
  const segs = url.pathname.split('/').filter(Boolean);
  const at = segs.indexOf('place') >= 0 ? segs.indexOf('place') : segs.indexOf('search');
  // /maps/place/<name>/@lat,lng,17z — the segment after place is the name, and
  // the @-segment after it is coordinates, which nobody wants to read.
  const named = at >= 0 ? segs[at + 1] : undefined;
  const place = named && !named.startsWith('@') ? prettifyPlace(named) : prettifyPlace(url.searchParams.get('q') ?? '');
  return {
    brand: 'Maps',
    variant: 'is-gmaps',
    title: place || 'Map',
    isJunkTitle: isGoogleJunkTitle,
  };
}

registerProvider((url): RichLink | null => {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const segs = url.pathname.split('/').filter(Boolean);

  if (host === 'docs.google.com' || host === 'sheets.google.com') {
    // /spreadsheets/d/<id>/edit and /spreadsheets/u/0/d/<id>/edit both start
    // with the product segment.
    const product = DOCS_PRODUCTS[(segs[0] ?? '').toLowerCase()];
    if (!product) return null;
    return {
      brand: product.brand,
      variant: product.variant,
      title: product.label,
      isJunkTitle: isGoogleJunkTitle,
    };
  }

  if (host === 'drive.google.com') {
    const isFolder = segs.includes('folders');
    return {
      brand: 'Drive',
      variant: 'is-gdrive',
      title: isFolder ? 'Folder' : 'File',
      isJunkTitle: isGoogleJunkTitle,
    };
  }

  if (host === 'maps.google.com' || host === 'maps.app.goo.gl' || (host === 'goo.gl' && segs[0] === 'maps')) {
    return mapsLink(url);
  }

  if (host === 'google.com' && segs[0] === 'maps') return mapsLink(url);

  return null;
});
